import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { ConstructionMasters } from "@/models/masters";
import { MatrixColumnKey } from "@/models/matrix";
import { RootState } from "@/reducers/types";

type Props = {
  displayRemarks: boolean;
};

const LeftGridHeader: FC<Props> = (props) => {
  const { t } = useTranslation();

  // 表示設定
  const { configColumns, currentConditions } = useSelector((state: RootState) => state.matrix);
  const show = useCallback((target): boolean => !configColumns || configColumns[target], [configColumns]);

  // 機器分類名・元請名
  const categoryId = currentConditions?.categoryId;
  const primaryChargeId = currentConditions?.primaryChargeId;
  const masters: ConstructionMasters = useSelector((state: RootState) => state.construction?.masters);
  const [categoryName, primaryChargeName] = useMemo(() => {
    if (!categoryId || !primaryChargeId || !masters?.categories || !masters?.primary_charges) {
      return ["", ""];
    }

    return [
      masters.categories.find((v) => v.category_id === categoryId)?.category_name ?? "",
      masters.primary_charges.find((v) => v.primary_charge_id === primaryChargeId)?.primary_charge_name ?? "",
    ];
  }, [categoryId, primaryChargeId, masters?.categories, masters?.primary_charges]);

  const [conditionLabelStyle, setConditionLabelStyle] = useState<{ maxWidth?: string; minWidth?: string }>();
  const tableRef = useRef<HTMLTableElement>();
  useEffect(() => {
    const minWidth = 200;
    const margin = 20;
    if (tableRef.current?.offsetWidth < minWidth + margin) {
      // テーブルの幅が狭い場合は表示幅も小さくする
      setConditionLabelStyle({ maxWidth: `${tableRef.current?.offsetWidth - margin}px` });
    } else {
      // テーブル幅に余裕があるときは基本的には200pxで表示する
      setConditionLabelStyle({ minWidth: `${minWidth}px`, maxWidth: `${tableRef.current?.offsetWidth - margin}px` });
    }
  }, [configColumns]);

  return (
    <table className="grid-table grid-table-header grid-table-left grid-table-left-header" ref={tableRef}>
      <thead>
        <tr>
          {show(MatrixColumnKey.No) && (
            <th className="col-no">
              <span className="grid-cell-narrow">No</span>
            </th>
          )}
          {show(MatrixColumnKey.DeliveryFlg) && (
            <th className="col-delivery-flg rotate">
              <span className="grid-cell-narrow">{t("delivery")}</span>
            </th>
          )}
          {show(MatrixColumnKey.ItemComplete) && (
            <th className="col-item-complete rotate">
              <span className="grid-cell-narrow">{t("complete_process")}</span>
            </th>
          )}
          {show(MatrixColumnKey.Totaltest) && (
            <th className="col-totaltest rotate">
              <span className="grid-cell-narrow">{t("airtight_preparation")}</span>
            </th>
          )}
          {show(MatrixColumnKey.Area) && <th className="col-area">{t("area")}</th>}
          {show(MatrixColumnKey.Device) && <th className="col-device">{t("device")}</th>}
          {show(MatrixColumnKey.PrimaryCharge) && <th className="col-primary-charge">{t("primary_charge")}</th>}
          {show(MatrixColumnKey.DocumentNo) && <th className="col-document-no">{t("construction_specification")}No</th>}
          <th className="col-item">{t("title")}</th>
          {show(MatrixColumnKey.Regulation) && <th className="col-regulation">{t("law")}</th>}
          {show(MatrixColumnKey.Systems) && <th className="col-systems">{t("system")}</th>}
          {show(MatrixColumnKey.Note) && <th className="col-note">{t("note")}</th>}
          {props.displayRemarks && <th style={{ maxWidth: 115, minWidth: 115 }}>{t("remarks")}</th>}
          {show(MatrixColumnKey.User) && <th className="col-user">{t("assignee")}</th>}
        </tr>
        <tr className="grid-separator-bg">
          {show(MatrixColumnKey.No) && <td className="grid-separator" />}
          {show(MatrixColumnKey.DeliveryFlg) && <td className="grid-separator" />}
          {show(MatrixColumnKey.ItemComplete) && <td className="grid-separator" />}
          {show(MatrixColumnKey.Totaltest) && <td className="grid-separator" />}
          {show(MatrixColumnKey.Area) && <td className="grid-separator" />}
          {show(MatrixColumnKey.Device) && <td className="grid-separator" />}
          {show(MatrixColumnKey.PrimaryCharge) && <td className="grid-separator" />}
          {show(MatrixColumnKey.DocumentNo) && <td className="grid-separator" />}
          <td className="grid-separator" />
          {show(MatrixColumnKey.Regulation) && <td className="grid-separator" />}
          {show(MatrixColumnKey.Systems) && <td className="grid-separator" />}
          {show(MatrixColumnKey.Note) && <td className="grid-separator" />}
          {props.displayRemarks && <td style={{ minWidth: 115, maxWidth: 115 }} className="grid-separator" />}
          {show(MatrixColumnKey.User) && <td style={{ minWidth: 60, maxWidth: 60 }} className="grid-separator" />}
        </tr>
        {categoryName && primaryChargeName && (
          <tr className="header-label" style={conditionLabelStyle}>
            <td>
              <span>{`${categoryName}：${primaryChargeName}`}</span>
            </td>
          </tr>
        )}
      </thead>
    </table>
  );
};

export default LeftGridHeader;
